import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Firebase configuration - these should be set in environment variables
const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID
};

// Initialize Firebase
let app = null;
let messaging = null;

function initializeFirebase() {
  if (app) {
    return app;
  }

  // Check if Firebase config is available
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
    console.warn('⚠️  Firebase configuration not found. Push notifications will be disabled.');
    console.warn('   Please set VUE_APP_FIREBASE_* environment variables');
    return null;
  }

  try {
    app = initializeApp(firebaseConfig);
    console.log('✅ Firebase initialized successfully');
    return app;
  } catch (error) {
    console.error('❌ Error initializing Firebase:', error);
    return null;
  }
}

function getMessagingInstance() {
  if (!app) {
    app = initializeFirebase();
  }
  
  if (!app) {
    return null;
  }

  if (!messaging) {
    try {
      messaging = getMessaging(app);
    } catch (error) {
      console.error('❌ Error getting messaging instance:', error);
      return null;
    }
  }

  return messaging;
}

// Request notification permission and get FCM token
async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return null;
  }

  const permission = await Notification.requestPermission();
  
  if (permission === 'granted') {
    return await getFCMToken();
  } else {
    console.warn('Notification permission denied');
    return null;
  }
}

const FCM_SW_URL = '/firebase-messaging-sw.js';
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';
const FCM_SW_TIMEOUT_MS = 5000;

function withTimeout(promise, ms, label) {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId);
  });
}

/**
 * Register / reuse the dedicated FCM messaging service worker.
 * Do NOT use the Workbox page-controlling SW — that caused PWA boot hangs.
 */
async function getServiceWorkerRegistration() {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const existing = await navigator.serviceWorker.getRegistration(FCM_SW_SCOPE);
    if (existing) {
      return existing;
    }

    return await withTimeout(
      navigator.serviceWorker.register(FCM_SW_URL, { scope: FCM_SW_SCOPE }),
      FCM_SW_TIMEOUT_MS,
      'FCM service worker registration'
    );
  } catch (error) {
    console.error('❌ Error getting FCM service worker registration:', error);
    return null;
  }
}

// Get FCM registration token (bound to the dedicated messaging service worker)
async function getFCMToken() {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    return null;
  }

  try {
    // VAPID key should be set in environment variable
    const vapidKey = process.env.VUE_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn('⚠️  VAPID key not found. Push notifications may not work.');
      console.warn('   Please set VUE_APP_FIREBASE_VAPID_KEY in your .env file');
    }

    const serviceWorkerRegistration = await getServiceWorkerRegistration();
    const tokenOptions = { vapidKey };
    if (serviceWorkerRegistration) {
      tokenOptions.serviceWorkerRegistration = serviceWorkerRegistration;
    }

    const token = await withTimeout(
      getToken(messagingInstance, tokenOptions),
      FCM_SW_TIMEOUT_MS,
      'FCM getToken'
    );

    if (token) {
      console.log('✅ FCM registration token obtained');
      return token;
    } else {
      console.warn('No registration token available');
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting FCM token:', error);
    return null;
  }
}

// Listen for foreground messages
function onForegroundMessage(callback) {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    return null;
  }

  try {
    return onMessage(messagingInstance, (payload) => {
      console.log('[PUSH_DEBUG] FCM message received in foreground', {
        type: payload?.data?.type,
        title: payload?.notification?.title || payload?.data?.title,
        body: payload?.notification?.body || payload?.data?.body,
        messageId: payload?.data?.messageId
      });
      if (callback) {
        callback(payload);
      }
    });
  } catch (error) {
    console.error('❌ Error setting up foreground message listener:', error);
    return null;
  }
}

// Check if notifications are supported
function isNotificationSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

// Check current notification permission
function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

export {
  initializeFirebase,
  getMessagingInstance,
  requestNotificationPermission,
  getFCMToken,
  getServiceWorkerRegistration,
  onForegroundMessage,
  isNotificationSupported,
  getNotificationPermission
};
